export type ConditionCategory =
  | 'pediatric'
  | 'pregnancy-women'
  | 'neurological'
  | 'adult-pain'
  | 'general-wellness'
  | 'special-populations';

export interface ConditionSection {
  heading: string;
  /** 1-3 short paragraphs, old-page length. May contain simple inline `<a href="/...">` links. */
  paragraphs: string[];
  image?: {
    src: string;
    alt: string;
    align?: 'left' | 'right';
  };
  /** 'two-column' splits paragraphs across the legacy `column.one_half` split; defaults to 'full'. */
  layout?: 'full' | 'two-column';
  /** Alternates the legacy `.full` (grey) / `.full.light` (cream) band backgrounds. */
  tint?: boolean;
}

export interface ConditionVideoData {
  youtubeId: string;
  title: string;
  description: string;
  poster: string;
  /** Actual YouTube publication date, in ISO 8601 format. */
  uploadDate: string;
  /** ISO 8601 duration, e.g. PT13M40S. */
  duration?: string;
}

export interface ConditionPageData {
  slug: string;
  /** Page headline; also the H1 and the meta/OG title. */
  title: string;
  /**
   * Short condition name for lists, the nav menu, condition search, related
   * links and blog topic labels, for pages whose `title` is a headline
   * sentence. Optional: pages with a short title just use `title`.
   */
  navLabel?: string;
  subtitle: string;
  /** Optional portrait video immediately above the free guide. */
  video?: ConditionVideoData;
  category: ConditionCategory;
  authorDoctorSchemaId?: string;
  heroImage?: string; // defaults to /images/home-og.webp
  seo: {
    title: string;
    description: string;
  };
  intro: string[];
  /** Inline circular photo floated right next to the intro paragraphs, matching the legacy `img.rounded.alignright` pattern. */
  introImage?: {
    src: string;
    alt: string;
  };
  /** A single italic, left-bordered pull quote rendered inside the narrative flow (legacy `.bldr_callout`). */
  pullQuote?: string;
  /**
   * Narrative sections (h2 + short paragraphs), the legacy essay-style body.
   * When present, ConditionPage renders ConditionNarrative instead of the
   * card-grid layout (symptoms/benefits/approach/outcomes are ignored).
   */
  sections?: ConditionSection[];
  /** The legacy "Saturday Appointments Available" grey callout box with one CTA button (`.bldr_notebox`). */
  calloutBox?: {
    heading: string;
    text: string;
    buttonText?: string;
    buttonLink?: string;
  };
  /** Optional 'Royal Oak and nearby communities' block (LocalAreaSection) after the narrative sections. */
  localArea?: { title: string; intro: string };
  highlightBox?: {
    icon?: string;
    label: string;
    text: string;
  };
  symptoms?: {
    sectionTitle: string;
    items: Array<{
      icon?: string;
      title: string;
      description: string;
    }>;
  };
  benefits?: {
    sectionTitle: string;
    items: Array<{
      icon?: string;
      title: string;
      description: string;
    }>;
  };
  approach?: {
    sectionTitle: string;
    description?: string;
    steps: Array<{
      title: string;
      description: string;
    }>;
  };
  outcomes?: {
    sectionTitle: string;
    items: string[];
  };
  faqs?: Array<{
    question: string;
    answer: string;
  }>;
  relatedConditions?: Array<{
    slug: string;
    title: string;
  }>;
  cta?: {
    title: string;
    description: string;
    buttonText: string;
  };
  schema?: {
    condition?: {
      name: string;
      description?: string;
      wikipediaUrl?: string;
    };
    therapy?: {
      name: string;
      description: string;
    };
    datePublished?: string;
    dateModified?: string;
    wordCount?: number;
  };
}

export const CATEGORY_LABELS: Record<ConditionCategory, string> = {
  'pediatric': 'Pediatric',
  'pregnancy-women': 'Pregnancy & Women',
  'neurological': 'Neurological',
  'adult-pain': 'Adult Pain',
  'general-wellness': 'General Wellness',
  'special-populations': 'Special Populations',
};

export const CATEGORY_ORDER: ConditionCategory[] = [
  'pediatric',
  'pregnancy-women',
  'neurological',
  'adult-pain',
  'general-wellness',
  'special-populations',
];
